/*
  assets/hrc-diagnostic-recommendations.js
  Motor de recomendación HRC. Prioriza la matriz editorial V1 sincronizada
  desde Google Sheets mediante product.metafields.hrc.recommendation_matrix_v1.
  Los productos sin matriz conservan una compatibilidad básica de respaldo.
*/
(function () {
  'use strict';

  var CFG = window.HRC_DIAGNOSTIC_CONFIG.recommendation;
  var MATRIX_VERSION = 'HRC_RECOMMENDATION_MATRIX_V1.0';

  var GOAL_AREA_MAP = {
    'entender-juego': ['player_game_understanding'],
    'mejorar-posicion': ['player_technical_tactical', 'player_physical_preparation'],
    'fisico': ['player_physical_preparation'],
    'mentalidad': ['player_mental_performance'],
    'dar-salto': ['player_technical_tactical', 'player_physical_preparation', 'player_mental_performance'],
    'definir-modelo': ['coach_game_model'],
    'mejorar-tareas': ['coach_methodology_tasks'],
    'planificar-mejor': ['coach_planning'],
    'analizar-mejor': ['coach_analysis_evaluation'],
    'liderar-comunicar': ['coach_leadership_communication'],
    'optimizar-proceso': ['coach_planning', 'coach_methodology_tasks']
  };

  var ROUTE_ORDER = ['free', 'foundation', 'development', 'advanced', 'application', 'complete'];
  var LEVEL_TO_STEP = {
    starting_point: 'free', developing: 'foundation', functional: 'development',
    advanced: 'advanced', consolidated: 'application'
  };

  function arrayOf(value) {
    if (Array.isArray(value)) return value;
    if (value === null || typeof value === 'undefined' || value === '') return [];
    return [value];
  }

  function csvIncludes(csv, value) {
    if (!csv || !value) return false;
    return (csv + '').split(',').map(function (s) { return s.trim(); }).indexOf(value) !== -1;
  }

  function includes(list, value) {
    return arrayOf(list).indexOf(value) !== -1;
  }

  function catalogAudienceOf(diagnosticAudience) {
    return diagnosticAudience === 'coach' ? 'trainer' : diagnosticAudience;
  }

  function audienceCompatible(product, diagnosticAudience) {
    var catalogAudience = catalogAudienceOf(diagnosticAudience);
    if (product.publico === catalogAudience) return true;
    return csvIncludes(product.audiences, catalogAudience);
  }

  function positionCompatible(product, position) {
    if (!position || position === 'all') return true;
    if (product.posicion === 'all' || !product.posicion) return true;
    if (product.posicion === position) return true;
    return csvIncludes(product.posicionesCompatibles, position);
  }

  function hasUsableCta(product) {
    if (product.availabilityStatus !== 'upcoming') return true;
    var klaviyoId = (product.klaviyoFormId || '').trim();
    var url = (product.waitlistUrl || '').trim();
    return !!(klaviyoId || (url && url !== '#'));
  }

  function matrixOf(product) {
    var matrix = product && product.recommendationMatrix;
    if (!matrix || typeof matrix !== 'object') return null;
    if (matrix.version !== MATRIX_VERSION) return null;
    return matrix;
  }

  function makeContext(scoringResult, context) {
    return {
      audience: scoringResult.audience,
      catalogAudience: catalogAudienceOf(scoringResult.audience),
      priority1: scoringResult.priority1.key,
      priority1Label: scoringResult.priority1.label,
      priority2: scoringResult.priority2 ? scoringResult.priority2.key : null,
      bottleneckLayer: scoringResult.bottleneck ? scoringResult.bottleneck.layer : null,
      generalLevelKey: scoringResult.generalLevel.key,
      goal: context.goal || null,
      position: context.position || null,
      moment: context.moment || null,
      role: context.role || null
    };
  }

  function routeStepScore(productStep, userLevelKey) {
    var target = LEVEL_TO_STEP[userLevelKey] || 'foundation';
    var ti = ROUTE_ORDER.indexOf(target);
    var pi = ROUTE_ORDER.indexOf(productStep);
    if (ti === -1 || pi === -1) return 0;
    var dist = Math.abs(ti - pi);
    if (dist === 0) return CFG.weights.routeStep;
    if (dist === 1) return Math.round(CFG.weights.routeStep * 0.6);
    return 0;
  }

  function legacyScore(product, ctx) {
    var w = CFG.weights;
    var score = 0;
    var reasons = [];
    if (csvIncludes(product.primaryAreas, ctx.priority1)) { score += w.primaryArea; reasons.push('area_principal'); }
    else if (csvIncludes(product.secondaryAreas, ctx.priority1)) { score += w.primaryArea * 0.5; reasons.push('area_principal_secundaria'); }
    if (ctx.priority2 && (csvIncludes(product.primaryAreas, ctx.priority2) || csvIncludes(product.secondaryAreas, ctx.priority2))) {
      score += w.secondPriority; reasons.push('segunda_prioridad');
    }
    if (ctx.bottleneckLayer && csvIncludes(product.transversalLayers, ctx.bottleneckLayer)) {
      score += w.transversalLayer; reasons.push('capa_transversal');
    }
    score += routeStepScore(product.routeStep, ctx.generalLevelKey);
    if (ctx.audience === 'player' && positionCompatible(product, ctx.position)) { score += w.positionOrRole; reasons.push('posicion'); }
    if (ctx.audience === 'coach') score += w.positionOrRole;
    var goalAreas = GOAL_AREA_MAP[ctx.goal] || [];
    if (goalAreas.some(function (g) { return csvIncludes(product.primaryAreas, g); })) {
      score += w.goal; reasons.push('objetivo');
    }
    if (ctx.moment && csvIncludes(product.seasonMoments, ctx.moment)) { score += w.season; reasons.push('temporada'); }
    if (product.recommendationPriority === 1) { score += w.editorialPriority; reasons.push('prioridad_editorial'); }
    return {
      product: product,
      matrix: null,
      score: Math.round(score),
      reasons: reasons,
      eligible: score >= CFG.thresholds.possibleComplement,
      mainEligible: score >= CFG.thresholds.possibleComplement,
      role: product.isFree ? 'free_resource' : 'primary_product',
      rejection: score >= CFG.thresholds.possibleComplement ? [] : ['below_threshold']
    };
  }

  function scoreFromMap(map, key) {
    if (!map || !key || typeof map[key] !== 'number') return 0;
    return map[key];
  }

  function gateMatrix(product, matrix, ctx) {
    var gates = matrix.gates || {};
    var rejection = [];
    if (arrayOf(gates.audiences).length && !includes(gates.audiences, ctx.catalogAudience)) rejection.push('audience_gate');
    if (includes(gates.excludeAudiences, ctx.catalogAudience)) rejection.push('audience_excluded');
    if (includes(gates.excludeAvailability, product.availabilityStatus)) rejection.push('availability_excluded');
    if (arrayOf(gates.positions).length && !includes(gates.positions, ctx.position)) rejection.push('position_gate');
    if (arrayOf(gates.seasons).length && !includes(gates.seasons, ctx.moment)) rejection.push('season_gate');
    var requiredPriorityAreas = arrayOf(gates.requiredAnyPriorityAreas);
    if (requiredPriorityAreas.length && !includes(requiredPriorityAreas, ctx.priority1) && !includes(requiredPriorityAreas, ctx.priority2)) {
      rejection.push('required_priority_area_gate');
    }
    return rejection;
  }

  function matrixScore(product, ctx) {
    var matrix = matrixOf(product);
    if (!matrix) return legacyScore(product, ctx);

    var rejection = gateMatrix(product, matrix, ctx);
    if (rejection.length) {
      return { product: product, matrix: matrix, score: 0, reasons: [], eligible: false, mainEligible: false, role: matrix.recommendationRole || 'primary_product', rejection: rejection };
    }

    var weights = matrix.weights || {};
    var score = 0;
    var reasons = [];
    var value;

    value = scoreFromMap(weights.primaryArea, ctx.priority1);
    if (value) { score += value; reasons.push('primary_area_priority1'); }
    else if (ctx.priority2) {
      value = scoreFromMap(weights.primaryArea, ctx.priority2);
      if (value) { score += value; reasons.push('primary_area_priority2'); }
    }

    value = scoreFromMap(weights.secondaryArea, ctx.priority1);
    if (value) { score += value; reasons.push('secondary_area_priority1'); }
    else if (ctx.priority2) {
      value = scoreFromMap(weights.secondaryArea, ctx.priority2);
      if (value) { score += value; reasons.push('secondary_area_priority2'); }
    }

    value = scoreFromMap(weights.goal, ctx.goal);
    if (value) { score += value; reasons.push('goal'); }
    value = scoreFromMap(weights.layer, ctx.bottleneckLayer);
    if (value) { score += value; reasons.push('layer'); }
    value = scoreFromMap(weights.position, ctx.position);
    if (value) { score += value; reasons.push('position'); }
    value = scoreFromMap(weights.season, ctx.moment);
    if (value) { score += value; reasons.push('season'); }
    value = scoreFromMap(weights.staffRole, ctx.role);
    if (value) { score += value; reasons.push('staff_role'); }

    score = Math.round(score);
    var role = matrix.recommendationRole || (product.isFree ? 'free_resource' : 'primary_product');
    var minimum = role === 'free_resource'
      ? (((matrix.thresholds || {}).minContextScore) || 0)
      : (matrix.minPaidScore || 45);
    var eligible = score >= minimum;
    var mainAreas = arrayOf((matrix.gates || {}).mainPriorityAreas);
    var mainEligible = eligible && role !== 'free_resource' && (!mainAreas.length || includes(mainAreas, ctx.priority1));
    if (!eligible) rejection.push('below_matrix_threshold');
    if (eligible && role !== 'free_resource' && !mainEligible) rejection.push('main_priority_gate');

    return {
      product: product,
      matrix: matrix,
      score: score,
      reasons: reasons,
      eligible: eligible,
      mainEligible: mainEligible,
      role: role,
      rejection: rejection
    };
  }

  var TEXT_TYPE_WEIGHT = {
    position_season: 100,
    position_specific: 80,
    role_specific: 75,
    goal_specific: 60,
    priority_specific: 50,
    base: 10
  };

  function selectMatrixText(matrix, ctx) {
    var texts = arrayOf(matrix && matrix.texts);
    if (!texts.length) return null;
    var candidates = texts.map(function (item, index) {
      if (item.position && arrayOf(item.position).length && !includes(item.position, ctx.position)) return null;
      if (item.season && item.season !== ctx.moment) return null;
      if (item.roles && arrayOf(item.roles).length && !includes(item.roles, ctx.role)) return null;
      var score = TEXT_TYPE_WEIGHT[item.type] || 0;
      if (item.goal && item.goal === ctx.goal) score += 25;
      if (item.area && item.area === ctx.priority1) score += 15;
      else if (item.area && item.area === ctx.priority2) score += 8;
      if (item.layer && item.layer === ctx.bottleneckLayer) score += 10;
      return { item: item, score: score, index: index };
    }).filter(Boolean);
    candidates.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.index - b.index;
    });
    return candidates.length ? candidates[0].item.text : null;
  }

  var LAYER_REASON_LABELS = {
    knowledge: 'trabaja la comprensión de base que necesitas reforzar primero',
    application: 'te ayuda a llevar a la práctica lo que ya sabes',
    consistency: 'te ayuda a mantener con regularidad lo que ya haces bien',
    demand: 'te ayuda a sostener tu nivel cuando aumenta la exigencia',
    review: 'te ayuda a revisar y ajustar tus decisiones para seguir mejorando'
  };

  var PRODUCT_REASON_OVERRIDES = {
    15627275927882: 'Te ayudará a mejorar tu orientación corporal para recibir más información, decidir antes y jugar con mayor seguridad.',
    15161939001674: 'Te ofrece rutinas prácticas para llegar al partido con mayor confianza, concentración y claridad mental.',
    10344445739338: 'Te ayudará a comprender mejor las responsabilidades y conductas clave de tu posición dentro de cada momento del juego.'
  };

  function buildLegacyReason(product, ctx) {
    if (Object.prototype.hasOwnProperty.call(PRODUCT_REASON_OVERRIDES, product.id)) return PRODUCT_REASON_OVERRIDES[product.id];
    var parts = [];
    if (csvIncludes(product.primaryAreas, ctx.priority1)) parts.push('conecta directamente con tu prioridad principal (' + ctx.priority1Label + ')');
    if (ctx.bottleneckLayer && csvIncludes(product.transversalLayers, ctx.bottleneckLayer)) {
      parts.push(LAYER_REASON_LABELS[ctx.bottleneckLayer] || 'encaja con el área donde ahora mismo tienes más margen de mejora');
    }
    if (ctx.audience === 'player' && ctx.position && product.posicion === ctx.position) parts.push('está adaptado a tu posición');
    if (!parts.length) parts.push('encaja con tu momento actual');
    return parts.join('; ') + '.';
  }

  function sortEntries(a, b) {
    if (b.score !== a.score) return b.score - a.score;
    var bp = b.matrix ? (b.matrix.editorialPriority || 0) : 0;
    var ap = a.matrix ? (a.matrix.editorialPriority || 0) : 0;
    if (bp !== ap) return bp - ap;
    return String(a.product.title || '').localeCompare(String(b.product.title || ''));
  }

  function excludedByPrimary(entry, primary) {
    if (!entry || !entry.matrix || !primary || !primary.matrix) return false;
    var excluded = arrayOf((entry.matrix.gates || {}).excludeIfPrimaryProduct);
    return includes(excluded, primary.matrix.productGid);
  }

  function toOutput(entry, slot, ctx) {
    if (!entry) return null;
    var reason = entry.matrix ? selectMatrixText(entry.matrix, ctx) : null;
    return {
      slot: slot,
      id: entry.product.id,
      productGid: entry.matrix ? entry.matrix.productGid : null,
      title: entry.product.title,
      url: entry.product.url,
      image: entry.product.image,
      price: entry.product.price,
      isFree: entry.product.isFree,
      availabilityStatus: entry.product.availabilityStatus,
      klaviyoFormId: entry.product.klaviyoFormId,
      waitlistUrl: entry.product.waitlistUrl,
      waitlistCta: entry.product.waitlistCta,
      score: entry.score,
      tier: entry.score >= 75 ? 'excellent' : (entry.score >= 60 ? 'solid' : 'possible_complement'),
      matrixVersion: entry.matrix ? entry.matrix.version : null,
      reason: reason || buildLegacyReason(entry.product, ctx)
    };
  }

  function classifyBaseRejection(product, ctx) {
    var reasons = [];
    if (!product || typeof product !== 'object' || !product.id || !product.title) return ['invalid_catalog_entry'];
    if (!product.diagnosticEnabled) reasons.push('diagnostic_disabled');
    if (product.availabilityStatus === 'hidden') reasons.push('unavailable');
    if (!audienceCompatible(product, ctx.audience)) reasons.push('audience_mismatch');
    if (!hasUsableCta(product)) reasons.push('missing_metadata');
    return reasons;
  }

  function recommend(catalogData, scoringResult, context) {
    var ctx = makeContext(scoringResult, context || {});
    var alertActive = scoringResult.alert && scoringResult.alert.active;

    var evaluated = catalogData.map(function (product) {
      var baseRejection = classifyBaseRejection(product, ctx);
      if (baseRejection.length) {
        return { product: product, matrix: matrixOf(product), score: 0, reasons: [], eligible: false, mainEligible: false, role: product.isFree ? 'free_resource' : 'primary_product', rejection: baseRejection };
      }
      return matrixScore(product, ctx);
    });

    var eligible = evaluated.filter(function (entry) { return entry.eligible; }).sort(sortEntries);
    var freeCandidates = eligible.filter(function (entry) { return entry.role === 'free_resource'; }).sort(sortEntries);
    var mainCandidates = eligible.filter(function (entry) {
      if (entry.role === 'free_resource' || !entry.mainEligible) return false;
      if (alertActive && csvIncludes(entry.product.primaryAreas, 'player_physical_preparation')) return false;
      return true;
    }).sort(sortEntries);

    var free = freeCandidates[0] || null;
    var main = mainCandidates[0] || null;
    var complement = null;
    if (main) {
      complement = eligible.filter(function (entry) {
        if (entry.role === 'free_resource') return false;
        if (entry.product.id === main.product.id) return false;
        if (free && entry.product.id === free.product.id) return false;
        if (excludedByPrimary(entry, main)) return false;
        return true;
      }).sort(sortEntries)[0] || null;
    }

    var upcoming = eligible.filter(function (entry) {
      return entry.product.availabilityStatus === 'upcoming' && hasUsableCta(entry.product);
    }).sort(sortEntries)[0] || null;

    var candidatesDebug = evaluated.map(function (entry) {
      var classification = 'excluded';
      if (free && free.product.id === entry.product.id) classification = 'free';
      else if (main && main.product.id === entry.product.id) classification = 'main';
      else if (complement && complement.product.id === entry.product.id) classification = 'complement';
      else if (upcoming && upcoming.product.id === entry.product.id) classification = 'upcoming';
      else if (entry.eligible) classification = 'eligible_not_selected';
      return {
        productTitle: entry.product && entry.product.title,
        matrixVersion: entry.matrix && entry.matrix.version,
        eligible: entry.eligible,
        mainEligible: entry.mainEligible,
        score: entry.score,
        matchedRules: entry.reasons,
        rejectedRules: entry.rejection,
        finalClassification: classification
      };
    });

    return {
      free: toOutput(free, 'free', ctx),
      main: toOutput(main, 'main', ctx),
      complement: toOutput(complement, 'complement', ctx),
      upcoming: toOutput(upcoming, 'upcoming', ctx),
      hasMatch: !!(free || main || complement),
      catalogEmpty: catalogData.length === 0,
      catalogSize: catalogData.length,
      matrixVersion: MATRIX_VERSION,
      _debug: candidatesDebug
    };
  }

  window.HRC_DIAGNOSTIC_RECOMMENDATIONS = {
    recommend: recommend,
    matrixVersion: MATRIX_VERSION,
    _matrixScore: matrixScore,
    _selectMatrixText: selectMatrixText
  };
})();
